// src/hooks/useFeatureFlag.ts
// Feature flag consumer hooks for CRM components.
//
// INVARIANT: Unknown flags return false (fail closed).

import { useAppSelector } from '../store/store';
import {
  selectFeatureFlag,
  selectFeatureFlagsStatus,
  selectSseConnected,
} from '../store/slices/featureFlagsSlice';
import type { CrmFeatureFlag } from '../api/centralFeaturesApi';

// ============================================================
// Single Flag Hook
// ============================================================

/**
 * Check if a feature flag is enabled.
 *
 * Returns false if:
 * - Flag is explicitly disabled
 * - Flag evaluation failed (fail closed)
 * - Flag key is unknown
 *
 * @param flagKey - Feature flag key (must be in CRM_FEATURE_FLAGS)
 * @returns boolean - Whether the flag is enabled
 *
 * @example
 * const aiEnabled = useFeatureFlag('crm.ai.assistant')
 * if (aiEnabled) {
 *   // Render AI features
 * }
 */
export function useFeatureFlag(flagKey: CrmFeatureFlag): boolean {
  return useAppSelector(selectFeatureFlag(flagKey));
}

// ============================================================
// Multiple Flags Hook
// ============================================================

/**
 * Check multiple feature flags at once.
 *
 * @param flagKeys - Array of feature flag keys
 * @returns Record mapping each key to its boolean value
 *
 * @example
 * const flags = useFeatureFlags(['crm.ai.assistant', 'crm.ai.email.compose'])
 * if (flags['crm.ai.assistant']) { ... }
 */
export function useFeatureFlags<T extends CrmFeatureFlag>(
  flagKeys: T[]
): Record<T, boolean> {
  const allFlags = useAppSelector((state) => state.featureFlags.flags);

  return flagKeys.reduce(
    (acc, key) => {
      // INVARIANT: Unknown flags return false (fail closed)
      acc[key] = allFlags[key] ?? false;
      return acc;
    },
    {} as Record<T, boolean>
  );
}

// ============================================================
// Status Hooks
// ============================================================

/**
 * Get feature flags loading status.
 * Useful for showing loading states during flag evaluation.
 *
 * @returns 'idle' | 'loading' | 'ready' | 'error'
 */
export function useFeatureFlagsStatus() {
  return useAppSelector(selectFeatureFlagsStatus);
}

/**
 * Get SSE connection status.
 * Useful for showing connection indicators in the UI.
 *
 * @returns boolean - Whether SSE is currently connected
 */
export function useSseConnected() {
  return useAppSelector(selectSseConnected);
}
